import { useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { useFormik } from 'formik';
import * as yup from 'yup';
import YupPassword from 'yup-password';

import Button from 'components/Button/Button';
import Input from 'components/Input/Input';

import useUrlQuery from '../../hooks/useUrlQuery';
import { required } from '../../utils/errorsTextHelper';
import Typography from '../Typography/Typography';

import { PasswordResetFormProps } from './ResetPasswordForm.types';

import './style.scss';
YupPassword(yup);

const ResetPasswordForm = ({
  setNewPassword,
  setModalState
}: PasswordResetFormProps) => {
  const token = useUrlQuery('token');
  const email = useUrlQuery('email');
  let resetPasswordSchema = yup.object().shape({
    password1: yup.string().password().required(required('Password')),
    password2: yup
      .string()
      .oneOf([yup.ref('password1'), null], 'Passwords must match')
      .required()
  });
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    submitForm,
    dirty,
    isValid
  } = useFormik({
    initialValues: {
      password1: '',
      password2: ''
    },
    validationSchema: resetPasswordSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      const data = {
        email,
        rawNewPassword: values.password1,
        token
      };
      setNewPassword(data, (success: boolean) => {
        setIsLoading(false);
        if (success) setModalState('login');
      });
    }
  });

  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  return (
    <div className="w-full">
      <div className="flex flex-col gap-y-2 text-center mb-6">
        <Typography tag="h4">Reset Password</Typography>
        <Typography className="text-p-s text-base-mutedForeground">
          Enter new password for your account. Make sure it's secure and easy to
          remember.
        </Typography>
      </div>
      <form
        className="flex flex-col gap-y-4"
        onSubmit={(e: React.SyntheticEvent) => {
          e.preventDefault();
        }}
        autoComplete="off"
      >
        <Input
          label="Email"
          placeholder="Email"
          name="email"
          value={email || ''}
          disabled
          autoComplete="off"
        />
        <div>
          <Input
            rightIcon={
              <FontAwesomeIcon
                onClick={() => setShowPassword1((prev) => !prev)}
                icon={
                  showPassword1 ? 'fa-solid fa-eye' : 'fa-solid fa-eye-slash'
                }
              />
            }
            type={showPassword1 ? 'text' : 'password'}
            label="New Password"
            placeholder="New Password"
            name="password1"
            value={values.password1}
            onChange={handleChange}
            onBlur={handleBlur}
            state={errors.password1 && touched.password1 ? 'error' : 'default'}
            description={
              errors.password1 && touched.password1 ? errors.password1 : ''
            }
            autoComplete="off"
          />
          <Typography className="mt-2 text-p-s text-base-mutedForeground">
            Your password must be at least 8 characters long and contain at
            least one capital letter (A-Z), one lowercase letter (a-z), and one
            numeral (0-9).
          </Typography>
        </div>
        <Input
          label="Confirm Password"
          placeholder="Confirm Password"
          name="password2"
          rightIcon={
            <FontAwesomeIcon
              onClick={() => setShowPassword2((prev) => !prev)}
              icon={showPassword2 ? 'fa-solid fa-eye' : 'fa-solid fa-eye-slash'}
            />
          }
          type={showPassword2 ? 'text' : 'password'}
          value={values.password2}
          onChange={handleChange}
          onBlur={handleBlur}
          state={errors.password2 && touched.password2 ? 'error' : 'default'}
          description={
            errors.password2 && touched.password2 ? errors.password2 : ''
          }
          autoComplete="off"
        />
        <Button
          disabled={!!Object.keys(errors).length || !isValid || !dirty}
          type="submit"
          onClick={submitForm}
          className="w-full"
          isLoading={isLoading}
        >
          Send
        </Button>
      </form>
      <Typography
        className="text-p-s cursor-pointer hover:opacity-80 underline text-center mt-9"
        onClick={() => setModalState('login')}
      >
        Go back to login page
      </Typography>
    </div>
  );
};

export default ResetPasswordForm;
